import { useAuthState } from '@/contexts/AuthProvider';
import { HELP_URL } from '@diritto/common/constants/url';
import { CategoryTypeById, CategoryTypeString } from '@diritto/common/content/constants';
import { EventListType } from '@diritto/common/event/constants';
import { PartnerDto } from '@diritto/common/partner/dtos/partner.dto';
import {
  ClickAction,
  ClickTargetType,
  TrackerExtraInfo,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { PartnerMenu } from 'app-components/accounts/profiles/menu/PartnerMenu';
import { ProfileMenu } from 'app-components/accounts/profiles/menu/ProfileMenu';
import { useTrackingState } from 'contexts/TrackingProvider';
import { EventNewBadgeIcon } from 'icons/badge/EventNewBadgeIcon';
import { useRouter } from 'next/navigation';
import { useGetUnreadChatMessageCount } from 'queries/chat/useGetUnreadChatMessageCount';
import { useGetEventsInfiniteQuery } from 'queries/events/useGetEventsInfiniteQuery';
import { useCallback, useMemo } from 'react';
import { Link } from 'ui-components/common/Link';
import { Menu, MenuBadge, MenuContainer, MenuItem, MenuWrapper } from 'ui-components/navigations/menu/Menu';
import { UserLoginMenu } from 'ui-components/navigations/menu/UserLoginMenu';

const MENU_STYLES = {
  countBadge: [
    'relative flex flex-row items-center justify-center px-1 py-[3px] rounded-[14px]',
    'bg-red-500 leading-[10px] text-[10px] font-medium text-white',
  ].join(' '),
  labelContainer: 'flex flex-row items-center justify-start gap-1',
  menuItem: 'flex flex-row items-center justify-between',
  menuItemLabel: 'text-xxs font-medium',
  menuItemDescription: 'text-xxxs font-medium text-gray-500',
};

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.UserMenu,
};

interface NormalUserMenuProps {
  profileId?: number;
  onBlur?: () => void;
  renderMenuItem: (
    href: string,
    action: ClickAction,
    label: string,
    badge?: React.ReactNode,
    description?: string,
  ) => React.JSX.Element;
}

const NormalUserMenu = ({ profileId, onBlur, renderMenuItem }: NormalUserMenuProps) => {
  const router = useRouter();
  const { profile } = useAuthState();
  const { data: eventsData } = useGetEventsInfiniteQuery({ listType: EventListType.Progress });
  const { data: unreadMsg } = useGetUnreadChatMessageCount(true);
  const isNewEventsExist = !!eventsData?.pages[0].events.length;

  const menuItems = useMemo(
    () => [
      { href: `/accounts/profiles/${profileId}`, action: ClickAction.ToMyProfile, label: '내 프로필' },
      {
        href: `/accounts/profiles/setting/favorite-category`,
        action: ClickAction.ToFavoriteCategory,
        label: '내 선호 장르',
        description: profile?.favoriteCategoryIds.map(id => CategoryTypeString[CategoryTypeById[id]]).join(', '),
      },
      { href: '/studio', action: ClickAction.ToDirittoStudio, label: '디리토 스튜디오' },
      {
        href: '/chats',
        action: ClickAction.ToMessageList,
        label: '메세지',
        badge: unreadMsg && unreadMsg?.totalUnreadCount > 0 && (
          <span className={MENU_STYLES.countBadge}>{unreadMsg.totalUnreadCount}</span>
        ),
      },
      {
        href: '/quest/attendance-check',
        action: ClickAction.ToAttendanceCheck,
        label: '출석체크',
        badge: <MenuBadge>리디포인트</MenuBadge>,
      },
      {
        href: '/events',
        action: ClickAction.ToEvent,
        label: '이벤트',
        badge: isNewEventsExist && <EventNewBadgeIcon />,
      },
      { href: HELP_URL, action: ClickAction.ToHelp, label: '고객센터' },
      { href: '/accounts/setting', action: ClickAction.ToSetting, label: '설정' },
    ],
    [profile, profileId, unreadMsg, isNewEventsExist],
  );

  return (
    <>
      <ProfileMenu onBlur={onBlur} onProfileChanged={router.refresh} />
      <MenuWrapper onClick={onBlur}>
        {menuItems.map(({ href, action, label, badge, description }) =>
          renderMenuItem(href, action, label, badge, description),
        )}
      </MenuWrapper>
    </>
  );
};

interface PartnerUserMenuProps {
  profileId?: number;
  onBlur?: () => void;
  renderMenuItem: (href: string, action: ClickAction, label: string, badge?: React.ReactNode) => React.JSX.Element;
}

const PartnerUserMenu = ({ profileId, onBlur, renderMenuItem }: PartnerUserMenuProps) => {
  const { data: unreadMsg } = useGetUnreadChatMessageCount(true);

  const menuItems = useMemo(
    () => [
      { href: `/accounts/profiles/${profileId}`, action: ClickAction.ToMyProfile, label: '내 프로필' },
      {
        href: '/chats/partner',
        action: ClickAction.ToMessageList,
        label: '메세지',
        badge: unreadMsg && unreadMsg?.totalUnreadCount > 0 && (
          <span className={MENU_STYLES.countBadge}>{unreadMsg.totalUnreadCount}</span>
        ),
      },
      { href: HELP_URL, action: ClickAction.ToHelp, label: '고객센터' },
      { href: '/accounts/setting', action: ClickAction.ToSetting, label: '설정' },
    ],
    [profileId, unreadMsg],
  );

  return (
    <>
      <PartnerMenu />
      <MenuWrapper onClick={onBlur}>
        {menuItems.map(({ href, action, label, badge }) => renderMenuItem(href, action, label, badge))}
      </MenuWrapper>
    </>
  );
};

interface UserMenuProps {
  isLoggedIn: boolean;
  isApp: boolean;
  profileId?: number;
  partner?: PartnerDto;
  onBlur?: () => void;
}

export const UserMenu = ({ isLoggedIn = false, isApp, profileId, partner, onBlur }: UserMenuProps) => {
  const { tracker } = useTrackingState();

  const handleTrack = useCallback(
    (action: ClickAction, clickType: ClickTargetType) => {
      tracker.track(TrackingEventType.Click, { action, clickType, trackerExtraInfo });
    },
    [tracker],
  );

  const renderMenuItem = useCallback(
    (href: string, action: ClickAction, label: string, badge?: React.ReactNode, description?: string) => (
      <Menu key={label}>
        <Link href={href} onClick={() => handleTrack(action, ClickTargetType.MoreMenu)}>
          <MenuItem className={MENU_STYLES.menuItem}>
            <span className={MENU_STYLES.labelContainer}>
              <span className={MENU_STYLES.menuItemLabel}>{label}</span>
              {badge}
            </span>
            {description && <span className={MENU_STYLES.menuItemDescription}>{description}</span>}
          </MenuItem>
        </Link>
      </Menu>
    ),
    [handleTrack],
  );

  if (isLoggedIn) {
    return (
      <MenuContainer>
        {partner ? (
          <PartnerUserMenu profileId={profileId} onBlur={onBlur} renderMenuItem={renderMenuItem} />
        ) : (
          <NormalUserMenu profileId={profileId} onBlur={onBlur} renderMenuItem={renderMenuItem} />
        )}
      </MenuContainer>
    );
  }

  return (
    <MenuContainer>
      <UserLoginMenu onBlur={onBlur} />
      <MenuWrapper onClick={onBlur}>
        {renderMenuItem(HELP_URL, ClickAction.ToHelp, '고객센터')}
        {isApp && renderMenuItem('/accounts/setting', ClickAction.ToSetting, '설정')}
      </MenuWrapper>
    </MenuContainer>
  );
};
